import { Checkbox } from '@mantine/core'
import * as questionnaires from '@seen/core/models/questionnaires/index'
import { ValidatedTool } from '@seen/core/models/validatedTool'
import type { ValidatedToolResponse } from '@seen/core/models/validatedToolResponse'
import { Badge } from '@seen/ui'
import type { JSX } from 'react'
import { z } from 'zod'
import { FormField } from '#src/components/core/form/FormField.tsx'
import { FormLabel } from '#src/components/core/form/FormLabel.tsx'
import { FormSubmitButton } from '#src/components/core/form/FormSubmitButton.tsx'
import { Input } from '#src/components/core/input/Input.tsx'
import { RichTextEditor } from '#src/components/rich-text-editor/RichTextEditor.tsx'
import { useValidatedToolForm } from '#src/components/validated-tool-form/shared/useValidatedToolForm.ts'
import {
  ValidatedToolInnerForm,
  type ValidatedToolInnerFormProps,
} from '#src/components/validated-tool-form/shared/ValidatedToolInnerForm.tsx'
import { ValidatedToolResponseCard } from '#src/components/validated-tool-form/shared/ValidatedToolResponseCard.tsx'
import { ValidatedToolSection } from '#src/components/validated-tool-form/shared/ValidatedToolSection.tsx'
import type { UseFormReturn } from '#src/hooks/useForm.ts'

export interface TUGFormProps extends Omit<
  ValidatedToolInnerFormProps,
  'validatedTool' | 'form' | 'subtitle'
> {
  response?: ValidatedToolResponse
  restorationId?: string
}

const schema = z.object({
  // https://loinc.org/89422-0
  '89423-8': z.number().min(1),
  'obs-1': z.boolean().nullable(),
  'obs-2': z.boolean().nullable(),
  'obs-3': z.boolean().nullable(),
  'obs-4': z.boolean().nullable(),
  'obs-5': z.boolean().nullable(),
  'obs-6': z.boolean().nullable(),
  'obs-7': z.boolean().nullable(),
  'obs-8': z.boolean().nullable(),
  summary: z.string().nullable(),
})

const validatedTool = new ValidatedTool(questionnaires.tugQuestionnaire)

export function TUGForm({
  response,
  restorationId,
  ...delegated
}: TUGFormProps): JSX.Element {
  const form = useValidatedToolForm({
    schema,
    validatedTool,
    response,
    restorationId,
  })

  return (
    <ValidatedToolInnerForm
      validatedTool={validatedTool}
      subtitle="For assessing mobility and fall risk"
      form={form}
      response={response}
      {...delegated}
    >
      {!response && (
        <div className="space-y-2 rounded-md p-4 shadow">
          <p>
            <b>Purpose</b>: To assess mobility
          </p>
          <p>
            <b>Equipment</b>: A stopwatch
          </p>
          <p>
            <b>Directions</b>: Patients wear their regular footwear and can use
            a walking aid, if needed. Begin by having the patient sit back in a
            standard arm chair and identify a line 3 meters, or 10 feet away, on
            the floor.
          </p>
          <div className="space-y-2">
            <p>
              <b>Instructions for patient:</b>
            </p>
            <div className="rounded-md bg-green-50 p-3">
              <p className="font-medium">
                When I say &quot;Go,&quot; I want you to:
              </p>
              <ol className="ml-5 list-decimal">
                <li>Stand up from the chair.</li>
                <li>Walk to the line on the floor at your normal pace.</li>
                <li>Turn.</li>
                <li>Walk back to the chair at your normal pace.</li>
                <li>Sit down again.</li>
              </ol>
            </div>
            <div className="rounded-full bg-yellow-100 px-4 py-2 text-sm">
              <strong>NOTE:</strong> Always stay by the patient for safety.
            </div>
          </div>
        </div>
      )}
      {!!response && (
        <ValidatedToolResponseCard response={response}>
          <Score form={form} />
          <FormField
            control={form.control}
            name="summary"
            render={({ field: { value, ...rest } }) =>
              !value ? null : (
                <RichTextEditor
                  defaultValue={value}
                  variant="unstyled"
                  placeholder="Add summary…"
                  contentClassName="max-w-(--container-prose)"
                  {...rest}
                  disabled={false}
                  editable={false}
                />
              )
            }
          />
        </ValidatedToolResponseCard>
      )}
      <div>
        <FormField
          control={form.control}
          name="89423-8"
          render={({ field: { value, ...rest } }) => (
            <>
              <FormLabel>Time in seconds</FormLabel>
              <Input
                {...rest}
                type="number"
                onChange={(e) => rest.onChange(Number(e.target.value))}
                value={value ?? ''}
                className="max-w-24"
                min={1}
                step={0.01}
              />
            </>
          )}
        />
      </div>
      <ValidatedToolSection
        title="Observations"
        subtitle="Observe the patient's postural stability, gait, stride length, and sway. "
      >
        <FormField
          control={form.control}
          name="obs-1"
          render={({ field: { value, ...rest } }) => (
            <Checkbox
              {...rest}
              onChange={(e) => rest.onChange(e.target.checked)}
              checked={!!value}
              label="Slow tentative pace"
            />
          )}
        />
        <FormField
          control={form.control}
          name="obs-2"
          render={({ field: { value, ...rest } }) => (
            <Checkbox
              {...rest}
              onChange={(e) => rest.onChange(e.target.checked)}
              checked={!!value}
              label="Loss of balance"
            />
          )}
        />
        <FormField
          control={form.control}
          name="obs-3"
          render={({ field: { value, ...rest } }) => (
            <Checkbox
              {...rest}
              onChange={(e) => rest.onChange(e.target.checked)}
              checked={!!value}
              label="Short strides"
            />
          )}
        />
        <FormField
          control={form.control}
          name="obs-4"
          render={({ field: { value, ...rest } }) => (
            <Checkbox
              {...rest}
              onChange={(e) => rest.onChange(e.target.checked)}
              checked={!!value}
              label="Little or no arm swing"
            />
          )}
        />
        <FormField
          control={form.control}
          name="obs-5"
          render={({ field: { value, ...rest } }) => (
            <Checkbox
              {...rest}
              onChange={(e) => rest.onChange(e.target.checked)}
              checked={!!value}
              label="Steadying self on walls"
            />
          )}
        />
        <FormField
          control={form.control}
          name="obs-6"
          render={({ field: { value, ...rest } }) => (
            <Checkbox
              {...rest}
              onChange={(e) => rest.onChange(e.target.checked)}
              checked={!!value}
              label="Shuffling"
            />
          )}
        />
        <FormField
          control={form.control}
          name="obs-7"
          render={({ field: { value, ...rest } }) => (
            <Checkbox
              {...rest}
              onChange={(e) => rest.onChange(e.target.checked)}
              checked={!!value}
              label="En bloc turning"
            />
          )}
        />
        <FormField
          control={form.control}
          name="obs-8"
          render={({ field: { value, ...rest } }) => (
            <Checkbox
              {...rest}
              onChange={(e) => rest.onChange(e.target.checked)}
              checked={!!value}
              label="Not using assistive device properly"
            />
          )}
        />
      </ValidatedToolSection>
      <Score form={form} />
      <ValidatedToolSection title="Summary">
        <FormField
          control={form.control}
          name="summary"
          render={({ field: { value, ...rest } }) => (
            <RichTextEditor
              defaultValue={value ?? ''}
              placeholder="Add summary…"
              contentClassName="max-w-(--container-prose)"
              {...rest}
            />
          )}
        />
      </ValidatedToolSection>
      {!response && (
        <div>
          <FormSubmitButton control={form.control}>Submit</FormSubmitButton>
        </div>
      )}
    </ValidatedToolInnerForm>
  )
}

function Score({ form }: { form: UseFormReturn<any> }): JSX.Element | null {
  const valuesByLinkId = form.watch()

  const time = valuesByLinkId['89423-8']

  if (!time) {
    return null
  }

  const isAtRisk = time >= 12
  const color = isAtRisk ? 'red' : 'green'
  const label = isAtRisk ? 'At risk' : 'Not at risk'

  return (
    <div className="flex items-center gap-2">
      <h2>
        Score: <b>{time}</b>
      </h2>
      <Badge color={color} className="mt-px">
        {label}
      </Badge>
    </div>
  )
}
